import { IServiceProvider } from "angular";
import { StateDeclaration, StateProvider, StateService, UrlRouterProvider, Ng1StateDeclaration, Transition } from "angular-ui-router";

// Apply a suffix to certain states to indicate that they will redirect to a state
// that is lazy-loaded.
const LAZY_STATE_SUFFIX = "lazy";

export interface RouteDeclaration {
    state: string;
    config: Ng1StateDeclaration;
}

export interface RouterHelper {
    configureStates(states: RouteDeclaration[], otherwisePath?: string): void;
    getStates(): StateDeclaration[];
}

// Source: https://github.com/johnpapa/angular-styleguide/blob/master/a1/README.md#style-y270
export class RouterHelperProvider implements IServiceProvider {

    constructor(private $stateProvider: StateProvider, private $urlRouterProvider: UrlRouterProvider) {
        'ngInject';
    }

    $get($state: StateService): RouterHelper {
        'ngInject';
        const { $stateProvider, $urlRouterProvider } = this;
        let hasOtherwise = false;
        return {
            configureStates(states, otherwisePath) {
                states.forEach((state: RouteDeclaration) => {
                    $stateProvider.state(state.state, state.config);
                });
                if (otherwisePath && !hasOtherwise) {
                    hasOtherwise = true;
                    $urlRouterProvider.otherwise(otherwisePath);
                }
            },
            getStates() {
                return $state.get();
            }
        }
    }
}

/**
 * Return a function that calls a router helper to configure the given list of states.
 * @param {any} states  The list of states to be configured.
 * @returns {function} The decorated configureStates method.
 */
export function configureStates(states: RouteDeclaration[], otherwise: string = null) {
    /**
     * Call configureStates from the injected provider on the states in scope.
     * @param routerHelper A router helper instance.
     */
    return function (routerHelper: RouterHelper) {
        'ngInject'
        routerHelper.configureStates(states, otherwise);
    }
}

/**
 * Generate a list of route declarations where the child is intended to be accessed through an extension
 * to a list of parent states. Useful for defining many identical states that are commonly reachable
 * from various other states, such as modal popups.
 * @param childBase The base state declaration for the child state.
 * @param parents The list of parent state names that will be extended to add the child as a substate.
 */
export function addChildExtensions(childBase: Partial<Ng1StateDeclaration>, parents: string[]): RouteDeclaration[] {
    return parents.map(state => ({
        state: `${state}.${childBase.name}`,
        config: {
            ...childBase,
            name: `${state}.${childBase.name}`
        }
    }));
}

/**
 * Return a ui-router lazy load function to initialize a module
 * with the name returned from the module promise function.
 * @param {any} modulePromiseFn A function returning a module name
 * @returns {any} The function that resolves when the module has finished loading.
 */
export function lazyLoad(modulePromiseFn) {
    return function ($transition$: Transition) {
        const $ocLazyLoad: oc.ILazyLoad = $transition$.injector().get("$ocLazyLoad");
        return modulePromiseFn().then(mod => lazyLoadModule($ocLazyLoad, mod));
    }
}

/**
 * Return a state deriving from a lazy-loaded state, which redirects to the
 * lazy-loaded state upon entry.
 */
export function lazyChild(state: string, url: string) {
    return {
        state: state + LAZY_STATE_SUFFIX,
        config: {
            url: url,
            redirectTo: state
        }
    }
}

export function resolveFromParam(paramName) {
    const res = function ($transition$: Transition) {
        return $transition$.params()[paramName];
    };
    // Manually annotate since ngAnnotate seems to return this as ["$transition$", function() { }] for some reason.
    res.$inject = ["$transition$"];
    return res;
}

/**
 * Load the module that was lazy loaded based on the given name using
 * the $ocLazyLoad service.
 * @param $ocLazyLoad An instance of the $ocLazyLoad service.
 * @param name The name of the module that was lazy loaded
 */
function lazyLoadModule($ocLazyLoad: oc.ILazyLoad, name: string) {
    return $ocLazyLoad.load([
        { name: name },
        // Load the template cache with "rerun" to process the template urls
        // loaded along with the module.
        { name: "apex.core.templateCache" }
    ], { rerun: true })
}
